import {
  ImportModuleBlankValues,
  ImportModuleBlankValueSuggestion,
  ImportModuleColumnProfileColumn,
  ImportModuleConceptField,
} from '../../../core/definitions/import-module';
import {ColumnUsage} from '../column-usages';

/**
 * The values read as blank and the values rejected as blank, per column,
 * and the column profile's suggestions for more. Values in one column are
 * compared ignoring case and surrounding space, as the backend reads a
 * cell against them.
 */

/** A value as the blank-value reader compares it. */
export function blankValueFold(value: string): string {
  return value.trim().toLowerCase();
}

/** Whether `a` and `b` are one value to the blank-value reader. */
export function sameBlankValue(a: string, b: string): boolean {
  return blankValueFold(a) === blankValueFold(b);
}

/** Whether `values` holds `value` (see `sameBlankValue`). */
export function hasBlankValue(values: readonly string[] | undefined, value: string): boolean {
  return !!values?.some(held => sameBlankValue(held, value));
}

/** `values` with `value` added to `column`'s list, or `values` itself when
 * the list holds it already. */
export function withBlankValue(
  values: ImportModuleBlankValues, column: string, value: string,
): ImportModuleBlankValues {
  if (hasBlankValue(values[column], value)) {
    return values;
  }
  return {...values, [column]: [...(values[column] ?? []), value.trim()]};
}

/** `values` without `value` in `column`'s list, and without the column
 * once its list is empty; `values` itself when the list does not hold
 * it. A column keeps its place among the others: the columns go out in
 * the order each was first given a value. */
export function withoutBlankValue(
  values: ImportModuleBlankValues, column: string, value: string,
): ImportModuleBlankValues {
  if (!hasBlankValue(values[column], value)) {
    return values;
  }
  return Object.fromEntries(Object.entries(values).flatMap(([held, list]) => {
    const kept = held === column ? list.filter(v => !sameBlankValue(v, value)) : list;
    return kept.length ? [[held, kept]] : [];
  }));
}

// --- suggestions ---------------------------------------------------------------------

/** A column profile suggestion to read a value as blank, in a column the
 * mapping reads. */
export interface BlankValueSuggestion extends ImportModuleBlankValueSuggestion {
  column: string;
  usages: ColumnUsage[];
  // `lookup_label_placeholder`: the lookup that gives the label.
  lookupName: string | null;
  // The term the value review found the value to name, when it did: the
  // value is then likely a real one.
  term: string | null;
  // What the usages do with the value unless it is read as blank.
  consequences: Phrase[];
}

export interface SuggestionSource {
  // The table's columns, in order.
  columns: readonly string[];
  profile: ReadonlyMap<string, ImportModuleColumnProfileColumn>;
  usages: ReadonlyMap<string, readonly ColumnUsage[]>;
  blankValues: ImportModuleBlankValues;
  rejected: ImportModuleBlankValues;
  // The name of the lookup that reads its codes from a column.
  lookupName: (column: string) => string | null;
  // The value review's scan, `[]` before one.
  reviewed: readonly ImportModuleConceptField[];
}

/** The open suggestions, column by column in table order: those for a
 * column the mapping reads, less a value read as blank or rejected in it
 * already. */
export function blankValueSuggestions(source: SuggestionSource): BlankValueSuggestion[] {
  const suggestions: BlankValueSuggestion[] = [];
  for (const column of source.columns) {
    const usages = source.usages.get(column);
    if (!usages?.length) {
      continue;
    }
    for (const suggestion of source.profile.get(column)?.suggestions ?? []) {
      if (hasBlankValue(source.blankValues[column], suggestion.value)
          || hasBlankValue(source.rejected[column], suggestion.value)) {
        continue;
      }
      suggestions.push({
        ...suggestion,
        column,
        usages: [...usages],
        lookupName: suggestion.reason === 'lookup_label_placeholder' ? source.lookupName(column) : null,
        term: reviewedTerm(source.reviewed, usages, suggestion.value),
        consequences: suggestionConsequences(suggestion, usages),
      });
    }
  }
  return suggestions;
}

/** The live term the value review offered for `value` in a concept field
 * that takes the column's cell as it stands, when it offered one by that
 * name. A value the scan matched to one term outright is not among its
 * rows, so this finds only the values it listed. */
function reviewedTerm(
  reviewed: readonly ImportModuleConceptField[], usages: readonly ColumnUsage[], value: string,
): string | null {
  const fieldIds = new Set(usages.filter(u => u.kind === 'concept' && u.whole).map(u => u.fieldId));
  for (const field of reviewed.filter(f => fieldIds.has(f.target_field))) {
    for (const row of field.review_rows.filter(r => sameBlankValue(r.source_value, value))) {
      const term = row.candidates.find(c =>
        !c.candidate_is_suggestion && c.candidate_valid !== false && sameBlankValue(c.candidate_name, value));
      if (term) {
        return term.candidate_name;
      }
    }
  }
  return null;
}

/** A translation key and its parameters. */
export interface Phrase {
  key: string;
  params?: {[name: string]: string | number};
}

/** What each of `usages` does with the suggested value unless it is read
 * as blank, one phrase per distinct outcome. Through a lookup the field
 * gets the lookup's label. */
export function suggestionConsequences(
  suggestion: ImportModuleBlankValueSuggestion, usages: readonly ColumnUsage[],
): Phrase[] {
  const phrases = new Map<string, Phrase>();
  for (const usage of usages) {
    const value = usage.viaLookup && suggestion.label ? suggestion.label : suggestion.value;
    const phrase = consequence(usage, value, suggestion.count);
    if (phrase) {
      phrases.set(JSON.stringify(phrase), phrase);
    }
  }
  return [...phrases.values()];
}

function consequence(usage: ColumnUsage, value: string, count: number): Phrase | null {
  const one = count === 1;
  switch (usage.kind) {
    case 'concept':
      return {key: 'TRANS__IMPORT__BLANK_VALUES__CONSEQUENCE_CONCEPT', params: {value}};
    case 'agent':
      return {
        key: one ? 'TRANS__IMPORT__BLANK_VALUES__CONSEQUENCE_AGENT_ONE' : 'TRANS__IMPORT__BLANK_VALUES__CONSEQUENCE_AGENT',
        params: {value, count},
      };
    case 'named_object':
      return {
        key: one
          ? 'TRANS__IMPORT__BLANK_VALUES__CONSEQUENCE_NAMED_OBJECT_ONE'
          : 'TRANS__IMPORT__BLANK_VALUES__CONSEQUENCE_NAMED_OBJECT',
        params: {value, count},
      };
    case 'field':
      return {
        key: one ? 'TRANS__IMPORT__BLANK_VALUES__CONSEQUENCE_FIELD_ONE' : 'TRANS__IMPORT__BLANK_VALUES__CONSEQUENCE_FIELD',
        params: {value, count, field: usage.field},
      };
    default:
      return null;
  }
}

/** How many cells of a column hold one of `values`, counted from the
 * column profile's most common values and its suggestions; `null` unless
 * each value is among them. */
export function blankCellCount(
  profile: ImportModuleColumnProfileColumn | undefined, values: readonly string[],
): number | null {
  if (!profile) {
    return null;
  }
  // Each spelling once: a suggestion may be among the most common values too.
  const counts = new Map<string, number>(
    [...profile.suggestions, ...profile.top].map(counted => [counted.value, counted.count]));
  let cells = 0;
  for (const value of values) {
    const counted = [...counts].filter(([spelling]) => sameBlankValue(spelling, value));
    if (!counted.length) {
      return null;
    }
    cells += counted.reduce((sum, [, count]) => sum + count, 0);
  }
  return cells;
}
